// components/produits/ProductForm.tsx
import { useState, useEffect } from 'react';
import axios from 'axios';
    import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';

interface ProductFormProps {
  product?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ProductForm({ product, onClose, onSuccess }: ProductFormProps) {
  const isEdit = !!product;

  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    description: '',
    barcode: '',
    category: '',
    unit: '',
    supplier: '',
    purchasePrice: '',
    salePrice: '',
    vat: '18',
    stockMin: '0',
    stockMax: '',
    stockSecurite: '',
    quantite: '',
    location: '',
    weight: '',
    perissable: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [categories, setCategories] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [emplacements, setEmplacements] = useState<any[]>([]);

 useEffect(() => {
  const fetchData = async () => {
    try {
      const [catRes, unitRes, supRes, empRes] = await Promise.all([
        getResources(`categories-produit`),
        getResources(`unites-mesure`),
        getResources(`fournisseurs`),
        getResources(`emplacements-stock`)
      ]);

      setCategories(catRes.data.data || catRes.data);
      setUnits(unitRes.data.data || unitRes.data);
      
      // Filtrer les fournisseurs actifs uniquement
      const activeSuppliers = (supRes.data.data || supRes.data).filter(
        (f: any) => f.actif === true
      );
      setSuppliers(activeSuppliers);

      setEmplacements(empRes.data.data || empRes.data);
    } catch (err) {
      console.error('Erreur chargement des listes', err);
    }
  };
  fetchData();
}, []);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        shortName: product.shortName || '',
        description: product.description || '',
        barcode: product.barcode || '',
        category: product.categorie_id?.toString() || '',
        unit: product.unite_id?.toString() || '',
        supplier: product.fournisseur_principal_id?.toString() || '',
        purchasePrice: product.purchasePrice?.toString() || '',
        salePrice: product.salePrice?.toString() || '',
        vat: product.vatSale?.toString() || '18',
        stockMin: product.stockMin?.toString() || '0',
        stockMax: product.stockMax?.toString() || '',
        stockSecurite: product.stock_securite?.toString() || '',
        location: product.location || '',
        weight: product.weight?.toString() || '',
        quantite: product.stockQuantity?.toString() || '',
        perissable: !!product.perishable,
      });
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const payload = {
      designation: formData.name.trim(),
      short_name: formData.shortName.trim(),
      description: formData.description.trim() || null,
      code_barre: formData.barcode.trim() || null,
      categorie_id: formData.category,
      unite_id: formData.unit,
      quantite: formData.quantite ? parseInt(formData.quantite) : 0,
      fournisseur_principal_id: formData.supplier,
      prix_achat_ht: parseFloat(formData.purchasePrice) || 0,
      prix_vente_ht: parseFloat(formData.salePrice) || 0,
      tva_percent: parseFloat(formData.vat),
      stock_min: parseInt(formData.stockMin) || 0,
      stock_max: formData.stockMax ? parseInt(formData.stockMax) : null,
      stock_securite: formData.stockSecurite ? parseInt(formData.stockSecurite) : null,
      poids_kg: formData.weight ? parseFloat(formData.weight) : null,
      perissable: formData.perissable,
      emplacement_defaut_code: formData.location.trim() || null,
    };

    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      };

      if (isEdit) {
        await updateResource(`produits`,product.id, payload);
      } else {
        await createResource(`produits`, payload);
      }

      onSuccess?.();
      onClose();
    } catch (err: any) {
      if (err.response?.status === 423) {
        alert(err.response.data.message);
        return;
      }
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const formatted: Record<string, string> = {};

        const map: Record<string, string> = {
          designation: 'name',
          short_name: 'shortName',
          description: 'description',
          code_barre: 'barcode',
          categorie_id: 'category',
          unite_id: 'unit',
          quantite: 'quantite',
          fournisseur_principal_id: 'supplier',
          prix_achat_ht: 'purchasePrice',
          prix_vente_ht: 'salePrice',
          tva_percent: 'vat',
          stock_min: 'stockMin',
          stock_max: 'stockMax',
          stock_securite: 'stockSecurite',
          poids_kg: 'weight',
          perissable: 'perissable',
          emplacement_defaut_code: 'location',
        };

        Object.keys(apiErrors).forEach(key => {
          formatted[map[key] || key] = apiErrors[key][0];
        });
        setErrors(formatted);
      }
    } finally {
      setLoading(false);
    }
  };

  const previewRef = isEdit ? product?.reference : `REF-${new Date().getFullYear()}-XXXXX`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        {/* En-tête */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Modifier le Produit' : 'Nouveau Produit'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* RÉFÉRENCE AUTO */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4">
              <label className="block text-sm font-semibold text-indigo-900 mb-2">
                Référence SKU (générée automatiquement)
              </label>
              <div className="px-5 py-3 bg-white border-2 border-dashed border-indigo-300 rounded-lg font-mono text-lg text-center font-bold text-indigo-700">
                {previewRef}
              </div>
            </div>

            {/* IDENTIFICATION */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-3 text-sm">Identification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">Code-barres</label>
                  <input type="text" value={formData.barcode} onChange={e => setFormData({ ...formData, barcode: e.target.value })} className="w-full px-3 py-2 border rounded-lg font-mono" placeholder="3274080005003" />
                  {errors.barcode && <p className="text-red-600 text-xs mt-1">{errors.barcode}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1">Unité *</label>
                  <select required value={formData.unit} onChange={e => setFormData({ ...formData, unit: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Choisir...</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.nom} ({u.symbole || u.code})</option>)}
                  </select>
                  {errors.unit && <p className="text-red-600 text-xs mt-1">{errors.unit}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block font-medium mb-1">Désignation longue *</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block font-medium mb-1">Désignation courte *</label>
                  <input type="text" required value={formData.shortName} onChange={e => setFormData({ ...formData, shortName: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="Eau minérale 1.5L x6" />
                  {errors.shortName && <p className="text-red-600 text-xs mt-1">{errors.shortName}</p>}
                </div>
              </div>
            </div>

            {/* CLASSIFICATION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <label className="block text-sm font-medium mb-2">Catégorie *</label>
                <select required value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Sélectionner...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
                {errors.category && <p className="text-red-600 text-xs mt-1">{errors.category}</p>}
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <label className="block text-sm font-medium mb-2">Fournisseur principal *</label>
                <select required value={formData.supplier} onChange={e => setFormData({ ...formData, supplier: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Choisir...</option>
                  {suppliers.map(f => <option key={f.id} value={f.id}>{f.raisonSociale}</option>)}
                </select>
                {errors.supplier && <p className="text-red-600 text-xs mt-1">{errors.supplier}</p>}
              </div>
            </div>

            {/* TARIFS */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-3 text-sm">Tarification</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">Prix achat HT *</label>
                  <input type="number" step="0.01" required value={formData.purchasePrice} onChange={e => setFormData({ ...formData, purchasePrice: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.purchasePrice && <p className="text-red-600 text-xs mt-1">{errors.purchasePrice}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1">Prix vente HT *</label>
                  <input type="number" step="0.01" required value={formData.salePrice} onChange={e => setFormData({ ...formData, salePrice: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.salePrice && <p className="text-red-600 text-xs mt-1">{errors.salePrice}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1">TVA (%)</label>
                  <select value={formData.vat} onChange={e => setFormData({ ...formData, vat: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                    {['0', '5.5', '10', '18', '20'].map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* STOCK & LOGISTIQUE */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h3 className="font-semibold text-orange-900 mb-3 text-sm">Stock</h3>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-medium mb-1">Min *</label>
                      <input type="number" required value={formData.stockMin} onChange={e => setFormData({ ...formData, stockMin: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block font-medium mb-1">Max</label>
                      <input type="number" value={formData.stockMax} onChange={e => setFormData({ ...formData, stockMax: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Stock sécurité</label>
                    <input type="number" value={formData.stockSecurite} onChange={e => setFormData({ ...formData, stockSecurite: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Quantité initiale</label>
                    <input type="number" value={formData.quantite} onChange={e => setFormData({ ...formData, quantite: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                    {errors.quantite && <p className="text-red-600 text-xs mt-1">{errors.quantite}</p>}
                  </div>
                  {/* <div>
                    <label className="block font-medium mb-1">Emplacement par défaut</label>
                    <input type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} className="w-full px-3 py-2 border rounded-lg font-mono text-xs" placeholder="A01-R1" />
                  </div> */}
                  <div>
                    <label className="block font-medium mb-1">Emplacement par défaut</label>
                    <select
                      value={formData.location}
                      onChange={e => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    >
                      <option value="">Aucun (optionnel)</option>
                      {emplacements.map(emp => (
                        <option key={emp.id} value={emp.code}>
                          {emp.code} - {emp.nom} ({emp.libelle})
                        </option>
                      ))}
                    </select>
                    {errors.location && <p className="text-red-600 text-xs mt-1">{errors.location}</p>}
                  </div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.perissable} onChange={e => setFormData({ ...formData, perissable: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                    <span className="text-sm font-medium">Produit périssable</span>
                  </label>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Logistique</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <label className="block font-medium mb-1">Poids (kg)</label>
                    <input type="number" step="0.01" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Description</label>
                    <textarea rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Informations supplémentaires..." />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Boutons fixes */}
        <div className="border-t border-gray-200 px-6 py-4 bg-white flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
            Annuler
          </button>
          <button type="submit" disabled={loading} onClick={handleSubmit} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-70">
            {loading ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer le produit'}
          </button>
        </div>
      </div>
    </div>
  );
}